import { logger } from '../utils/logger';
import { makeAuthenticatedRequest } from '../utils/api';
import { getPlayerStats, PlayerStats } from '../utils/achievements';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTutorial } from '../contexts/TutorialContext';
import VersionInfo from './VersionInfo';
import Characters from './Characters';
import HexWorldMap from './HexWorldMap';
import BattleGrid from './BattleGrid';
import Crafting from './Crafting';
import Achievements from './Achievements';
import AdminDashboard from './AdminDashboard';
import HelpOverlay from './HelpOverlay';
import ErrorBoundary from './ErrorBoundary';
import { useConfirm } from './ConfirmModal';
import TutorialLibrary from './TutorialLibrary';
import QuestProgressPanel from './QuestProgressPanel';
import UserSettings from './UserSettings';

type ActiveScreen = 'home' | 'characters' | 'hexworld' | 'crafting' | 'achievements' | 'admin' | 'battle' | 'settings';

const Home: React.FC = () => {
  const { user, logout } = useAuth();
  const { addNotification } = useNotifications();
  const { startTutorial } = useTutorial();
  const [activeScreen, setActiveScreen] = useState<ActiveScreen>('home');
  const [currentBattleId, setCurrentBattleId] = useState<number | null>(null);
  const [isCreatingStarter, setIsCreatingStarter] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showTutorialLibrary, setShowTutorialLibrary] = useState(false);
  const [activeBattle, setActiveBattle] = useState<{ id: number; name: string } | null>(null);
  const [isCheckingBattle, setIsCheckingBattle] = useState(true);
  const [playerStats, setPlayerStats] = useState<PlayerStats | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  // Hard-block navigation while a battle is in progress
  const handleNavigation = (newScreen: ActiveScreen) => {
    if (activeScreen === 'battle' && newScreen !== 'battle' && currentBattleId) {
      return;
    }
    setActiveScreen(newScreen);
  };

  // Helper function to handle logout with battle warning
  const handleLogout = async () => {
    // If currently in battle, show warning
    if (activeScreen === 'battle' && currentBattleId) {
      const confirmed = await confirm({
        title: 'Abandon Battle?',
        message: "You're currently in battle! Logging out will abandon the fight.\n\nAre you sure you want to logout?",
        confirmText: 'Logout',
        danger: true,
      });
      if (!confirmed) {
        return; // Don't logout
      }
    }
    logout();
  };

  useEffect(() => {
    const checkAndCreateStarterCharacter = async () => {
      if (!user) return;

      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        // Check if user has any characters
        const response = await makeAuthenticatedRequest('/characters');

        if (response.ok) {
          const characters = await response.json();

          // If no characters, create a starter character
          if (characters.length === 0) {
            setIsCreatingStarter(true);

            const createResponse = await makeAuthenticatedRequest('/characters/create-starter', {
              method: 'POST'
            });

            if (createResponse.ok) {
              setShowWelcome(true);
              setTimeout(() => setShowWelcome(false), 32000);

              // Show notification toast
              addNotification({
                type: 'info',
                title: 'Welcome to Heroes of the Grid!',
                message: 'Your first hero has been created. Begin your adventure!'
              });
            }

            setIsCreatingStarter(false);
          }
        }
      } catch (error) {
        logger.error('Error checking/creating starter character:', error);
        setIsCreatingStarter(false);
      }
    };

    checkAndCreateStarterCharacter();
  }, [user]);

  useEffect(() => {
    const checkActiveBattle = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsCheckingBattle(false);
        return;
      }
      try {
        const res = await makeAuthenticatedRequest('/battles/active');
        if (res.ok) {
          const data = await res.json();
          const battle = data.activeBattle ?? null;
          setActiveBattle(battle);
          if (battle) {
            setCurrentBattleId(battle.id);
            setActiveScreen('battle');
          }
        }
      } catch { /* non-critical */ }
      finally {
        setIsCheckingBattle(false);
      }
    };
    checkActiveBattle();
  }, [user]);

  // Load player stats for the home dashboard whenever the home screen is shown
  useEffect(() => {
    if (!user || activeScreen !== 'home') return;
    getPlayerStats().then(setPlayerStats).catch(err => {
      logger.error('Error loading player stats:', err);
    });
  }, [user, activeScreen]);

  const renderContent = () => {
    switch (activeScreen) {
      case 'characters':
        return <Characters />;
      case 'hexworld':
        return <HexWorldMap onStartBattle={(battleId) => {
          setCurrentBattleId(battleId);
          setActiveScreen('battle');
        }} />;
      case 'battle':
        return currentBattleId ? (
          <ErrorBoundary
            title="The battle hit an unexpected error"
            resetLabel="Return to World Map"
            onReset={() => {
              setCurrentBattleId(null);
              setActiveBattle(null);
              setActiveScreen('hexworld');
            }}
          >
            <BattleGrid
              battleId={currentBattleId}
              onExitBattle={() => {
                setCurrentBattleId(null);
                setActiveBattle(null);
                setActiveScreen('hexworld');
              }}
            />
          </ErrorBoundary>
        ) : (
          <div className="battle-error">
            <p>No battle ID available. Please return to world map and start a new battle.</p>
            <button onClick={() => setActiveScreen('hexworld')}>Return to World Map</button>
          </div>
        );
      case 'crafting':
        return <Crafting />;
      case 'achievements':
        return <Achievements />;
      case 'settings':
        return <UserSettings />;
      case 'admin':
        return <AdminDashboard />;
      default:
        return (
          <div className="home-dashboard">
            {showWelcome && (
              <div className="welcome-banner">
                <h2>Welcome to Heroes of the Grid!</h2>
                <p>We've created your first hero to get you started. Visit the <strong>Characters</strong> page to view your hero, then explore the <strong>World Map</strong> to begin your adventure!</p>
              </div>
            )}
            {isCreatingStarter && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <p>Creating your first hero...</p>
              </div>
            )}
            <div className="welcome-section">
              <h2>{showWelcome ? `Welcome, ${user?.username}!` : `Welcome back, ${user?.username}!`}</h2>
              <p>Your turn-based tactical adventure awaits. Choose your path:</p>
              
              <div className="quick-actions">
                <div className="action-card action-card-characters" onClick={() => handleNavigation('characters')}>
                  <div className="card-icon">⚔️</div>
                  <h3>Characters</h3>
                  <p>Manage your heroes and unlock their potential</p>
                  <div className="card-hint">Click to view →</div>
                </div>
                <div className="action-card action-card-hexworld" onClick={() => handleNavigation('hexworld')}>
                  <div className="card-icon">🗺️</div>
                  <h3>World Map</h3>
                  <p>Explore a vast procedural world with resources and battles</p>
                  <div className="card-hint">Explore world →</div>
                </div>
                <div className="action-card action-card-crafting" onClick={() => handleNavigation('crafting')}>
                  <div className="card-icon">🔨</div>
                  <h3>Crafting</h3>
                  <p>Forge powerful equipment from gathered materials</p>
                  <div className="card-hint">Create items →</div>
                </div>
                <div className="action-card action-card-achievements" onClick={() => handleNavigation('achievements')}>
                  <div className="card-icon">🏆</div>
                  <h3>Achievements</h3>
                  <p>Track your progress and unlock rewards</p>
                  <div className="card-hint">View achievements →</div>
                </div>
              </div>

              <QuestProgressPanel />
            </div>

            {playerStats && (
              <div className="recent-activity">
                <h3>Your Adventure</h3>
                <div className="activity-list">
                  <div className="activity-item">
                    <span className="activity-icon">⚔️</span>
                    <div className="activity-text">
                      <p>{playerStats.battlesWon} {playerStats.battlesWon === 1 ? 'battle' : 'battles'} won</p>
                      <small>{playerStats.monstersDefeated} monsters defeated · {playerStats.totalDamageDealt.toLocaleString()} total damage dealt</small>
                    </div>
                  </div>
                  <div className="activity-item">
                    <span className="activity-icon">🔨</span>
                    <div className="activity-text">
                      <p>{playerStats.itemsCrafted} {playerStats.itemsCrafted === 1 ? 'item' : 'items'} crafted</p>
                      <small>{playerStats.recipesDiscovered} {playerStats.recipesDiscovered === 1 ? 'recipe' : 'recipes'} discovered</small>
                    </div>
                  </div>
                  <div className="activity-item">
                    <span className="activity-icon">🗺️</span>
                    <div className="activity-text">
                      <p>{playerStats.tilesExplored} {playerStats.tilesExplored === 1 ? 'tile' : 'tiles'} explored</p>
                      <small>{playerStats.materialsGathered} materials gathered</small>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
    }
  };

  if (isCheckingBattle) {
    return (
      <div className="home">
        <div className="battle-lock-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="home">
      {confirmDialog}
      {showHelp && <HelpOverlay onClose={() => setShowHelp(false)} />}
      {showTutorialLibrary && <TutorialLibrary onClose={() => setShowTutorialLibrary(false)} />}
      <header className="home-header">
        <h1>Heroes of the Grid</h1>
        <div className="user-info">
          <button onClick={() => setShowTutorialLibrary(true)} className="tutorial-test-button" title="Tutorial Library">
            📚
          </button>
          <button onClick={() => setShowHelp(true)} className="help-button" title="How to Play">
            ?
          </button>
          <span>Welcome, {user?.username}!</span>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>
      
      <nav className="main-nav">
        <button
          className={`nav-button ${activeScreen === 'home' ? 'active' : ''}`}
          onClick={() => handleNavigation('home')}
        >
          Home
        </button>
        <button
          className={`nav-button ${activeScreen === 'characters' ? 'active' : ''}`}
          onClick={() => handleNavigation('characters')}
        >
          Characters
        </button>
        <button
          className={`nav-button ${activeScreen === 'hexworld' ? 'active' : ''}`}
          onClick={() => handleNavigation('hexworld')}
        >
          World Map
        </button>
        <button
          className={`nav-button ${activeScreen === 'crafting' ? 'active' : ''}`}
          onClick={() => handleNavigation('crafting')}
        >
          Crafting
        </button>
        <button
          className={`nav-button ${activeScreen === 'achievements' ? 'active' : ''}`}
          onClick={() => handleNavigation('achievements')}
        >
          Achievements
        </button>
        <button
          className={`nav-button ${activeScreen === 'settings' ? 'active' : ''}`}
          onClick={() => handleNavigation('settings')}
        >
          Settings
        </button>
        {user?.role === 'Admin' && (
          <button
            className={`nav-button ${activeScreen === 'admin' ? 'active' : ''}`}
            onClick={() => handleNavigation('admin')}
          >
            Admin Panel
          </button>
        )}
      </nav>
      
      <main className="main-content">
        {renderContent()}
      </main>
      
      <footer className="version-footer">
        <VersionInfo />
      </footer>
    </div>
  );
};

export default Home;